"use client";

import type { FC } from "react";
import { UserIcon } from "@heroicons/react/20/solid";
import { Button } from "@/components/button";
import { LocateFixedIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

type DirectoryEmptyStateProps = {
  description: string;
  onSearch: () => void;
  onNearMe: () => void;
  onClearFilters?: () => void;
  isLocating?: boolean;
  className?: string;
};

export const DirectoryEmptyState: FC<DirectoryEmptyStateProps> = ({
  description,
  onSearch,
  onNearMe,
  onClearFilters,
  isLocating,
  className,
}) => (
  <div
    className={cn(
      "flex w-full flex-col items-center justify-center gap-24 overflow-hidden rounded-medium border border-neutral-black/5 bg-neutral-white p-32 text-center lg:p-80",
      className,
    )}
  >
    <div aria-hidden className="relative h-62 w-60 shrink-0 overflow-hidden rounded-small bg-tone-aqua-tint">
      <UserIcon className="absolute top-5 -left-10 size-80 text-tone-steel" />
    </div>

    <div className="flex flex-col items-center gap-8">
      <h2 className="text-32 leading-[1.3] font-semibold text-neutral-black">No practitioners found</h2>
      <p className="max-w-398 text-14 leading-[1.4] text-neutral-black/70">{description}</p>
    </div>

    <div className="flex flex-wrap items-center justify-center gap-24 lg:gap-32">
      <Button variant="primary-blue" size="l" onClick={onSearch}>
        Search
      </Button>
      <button
        type="button"
        onClick={onNearMe}
        disabled={isLocating}
        aria-label="Use my current location"
        className="group flex shrink-0 cursor-pointer items-center gap-8 text-18 font-medium leading-[1.4] text-neutral-black disabled:opacity-50"
      >
        <LocateFixedIcon
          aria-hidden
          className={cn("size-28 shrink-0 text-brand-blue", isLocating && "animate-pulse")}
        />
        Near Me
      </button>
    </div>

    {onClearFilters && (
      <button
        type="button"
        onClick={onClearFilters}
        className="cursor-pointer text-16 font-medium leading-[1.4] text-brand-blue hover:underline"
      >
        Clear all filters
      </button>
    )}
  </div>
);
